import React, { useContext } from "react";

import { QuickAddProps } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddProps";
import { BrandPageQuickAddTrackingEventsContextProvider } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddTrackingEventsContext/__internal__/QuickAddTrackingEventsProviders/BrandPageQuickAddTrackingEventsContextProvider";
import { LayoutElementQuickAddTrackingEventsContextProvider } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddTrackingEventsContext/__internal__/QuickAddTrackingEventsProviders/LayoutElementQuickAddTrackingEventsContextProvider";
import { QuickAddTrackingEventsContextBaseProvider } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddTrackingEventsContext/__internal__/QuickAddTrackingEventsProviders/QuickAddTrackingEventsContextBaseProvider";
import { ProductTileType } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/ProductTileType";
import { ProductTileTypeContext } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/productTileTypeContext";

/**
 * This component is responsible for providing the QuickAddTrackingEventsContext to the QuickAdd component.
 * The returned component should provide callbacks include:
 * - onQuickAddButtonClick
 * - onQuickAddSubmit
 * - onChooseOptionClick
 * - onChooseOptionQuantityUpdateSubmit
 *
 * By default, the <QuickAddTrackingEventsContextBaseProvider/> provides impression tracking
 * for all quick-add related events.
 *
 * In case you want to add custom tracking events, do the following:
 * - Create a type for your product tile
 * - Create a new context provider by extending <QuickAddTrackingEventsContextBaseProvider/>
 *  - Do whatever you want in the provider
 * - Add your context provider in the switch statement below
 * Done!
 *
 * This component is mainly used for tracking events. It's not encouraged to provide business logic here.
 * Common scenarios include:
 * - Do X when product quantity updated => do it via cart state as much as possible
 * But if you have to do something before the quantity updatedin cart besides tracking,
 *  you can do it here as well, but that should be VERY RARE.
 *
 * @param param0 data inherits what we provided for the quick add component
 * @returns
 */
export const QuickAddTrackingEventsContextProvider = ({
  data,
  children,
}: {
  data: QuickAddProps;
  children: React.ReactNode;
}) => {
  const { productTileType } = useContext(ProductTileTypeContext);

  switch (productTileType) {
    case ProductTileType.LAYOUT_ELEMENT:
      return (
        <LayoutElementQuickAddTrackingEventsContextProvider data={data}>
          {children}
        </LayoutElementQuickAddTrackingEventsContextProvider>
      );
    case ProductTileType.BRAND:
      return (
        <BrandPageQuickAddTrackingEventsContextProvider data={data}>
          {children}
        </BrandPageQuickAddTrackingEventsContextProvider>
      );
    case ProductTileType.SEARCH:
    default:
      return (
        <QuickAddTrackingEventsContextBaseProvider data={data}>
          {children}
        </QuickAddTrackingEventsContextBaseProvider>
      );
  }
};
